import React, {
  FC,
  MouseEvent,
  MutableRefObject,
  TouchEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import tinycolor from 'tinycolor2';

import { getGradient, rgbaToArray, rgbaToHex } from '../../../../utils';

import { IPropsPanel, TCoords } from './types';

const Markers: FC<IPropsPanel> = ({
  color,
  setColor,
  activeColor,
  setActiveColor,
  setInit,
  format = 'rgb',
  showAlpha = true,
  allowAddGradientStops = true
}) => {
  const node = useRef() as MutableRefObject<HTMLDivElement>;

  const [needDeleteActive, setNeedDeleteActive] = useState<boolean>(false);
  const [hideStop, setHideStop] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const { stops, type, modifier } = color;

  const onAddColorStop = (e: MouseEvent) => {
    setInit(false);
    e.stopPropagation();
    if (!allowAddGradientStops) return;
    const target = e.target as HTMLElement;

    if (target.className !== 'gradient-marker') {
      const rect = target.getBoundingClientRect();
      const clickPos = e.clientX - rect.left;
      const loc = Number(((100 / rect.width) * clickPos).toFixed(0)) / 100;
      const rgba = tinycolor(activeColor.hex);
      rgba.setAlpha(activeColor.alpha / 100);

      const newStops = [
        ...color.stops,
        [rgba.toRgbString(), loc, color.stops.length]
      ]
        .sort((a: [string, number], b: [string, number]) => a[1] - b[1])
        .map((item, index) => {
          item[2] = index;
          return item;
        });

      setColor({
        ...color,
        gradient: `${getGradient(type, newStops, modifier, format, showAlpha)}`,
        stops: newStops
      });

      const newStopIndex = newStops.find((item) => item[1] === loc)?.[2];
      const newStopColor = newStops.find((item) => item[1] === loc)?.[0];
      const newStopRgba = rgbaToArray(newStopColor || rgba.toRgbString());
      const newStopHex = rgbaToHex([
        newStopRgba[0],
        newStopRgba[1],
        newStopRgba[2]
      ]);
      const newStopAlpha = Math.round(newStopRgba[3] * 100);

      const newActiveColor = {
        hex: newStopHex,
        alpha: newStopAlpha,
        loc: loc,
        index: newStopIndex
      };

      setActiveColor(newActiveColor);
    }
  };

  const removeListeners = () => {
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', onDragEnd);
  };

  const removeTouchListeners = () => {
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  };

  const onMouseDown = (e: MouseEvent, color: any) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    if (e.detail === 2) {
      return;
    }

    setInit(false);

    if (e.button !== 0) return;

    const newColor = tinycolor(color[0]);

    setActiveColor({
      hex: '#' + newColor.toHex(),
      alpha: newColor.getAlpha() * 100,
      loc: color[1],
      index: color[2]
    });

    const x = e.clientX;
    const y = e.clientY;

    // Don't set isDragging immediately - wait for actual mouse movement

    pointMoveTo({
      x,
      y
    });

    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', onDragEnd);
  };

  const onDrag = (e: any) => {
    // Defensive check for event object
    if (!e) {
      console.warn('onDrag called with undefined event object');
      return;
    }

    // More defensive check for component and node availability
    if (!node || !node.current) {
      console.warn(
        'Unable to get bounding rect: component reference unavailable'
      );
      // Clean up event listeners if component is no longer mounted
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', onDragEnd);
      return;
    }

    const x = e.clientX || 0;
    const y = e.clientY || 0;

    let rect;
    try {
      rect = node.current.getBoundingClientRect();
    } catch (error) {
      console.warn('Unable to get bounding rect for drag operation:', error);
      // Clean up event listeners on error
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', onDragEnd);
      return;
    }

    if (!rect) {
      console.warn(
        'Unable to get bounding rect: getBoundingClientRect returned null'
      );
      return;
    }

    const rootDistance = y - (rect.y || rect.top);

    // Now we know user is actually dragging
    setIsDragging(true);

    if (rootDistance > 80 && stops.length > 2) {
      setHideStop(true);
      return;
    } else {
      setHideStop(false);
    }

    pointMoveTo({
      x,
      y
    });
  };

  const onDragEnd = (e: any) => {
    // Always remove listeners first, regardless of errors
    removeListeners();
    setIsDragging(false); // Mark as not dragging

    // Defensive event and component checks
    if (!e) {
      console.warn('onDragEnd called with undefined event object');
      return;
    }

    if (!node || !node.current) {
      console.warn('onDragEnd: component reference unavailable');
      return;
    }

    const x = e.clientX || 0;
    const y = e.clientY || 0;

    let rect;
    try {
      rect = node.current.getBoundingClientRect();
    } catch (error) {
      console.warn('Unable to get bounding rect in onDragEnd:', error);
      return;
    }

    if (!rect) {
      console.warn('onDragEnd: getBoundingClientRect returned null');
      return;
    }

    const rootDistance = y - (rect.y || rect.top);
    if (rootDistance > 80 && stops.length > 2) {
      setNeedDeleteActive(true);
    }

    pointMoveTo({
      x,
      y
    });
  };

  const onTouchStart = (e: TouchEvent, color: any) => {
    setInit(false);

    if (e.cancelable) {
      e.preventDefault();
    }

    if (e.touches.length !== 1) {
      return;
    }

    removeTouchListeners();

    const newColor = tinycolor(color[0]);
    setActiveColor({
      hex: '#' + newColor.toHex(),
      alpha: newColor.getAlpha() * 100,
      loc: color[1],
      index: color[2]
    });

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    // Don't set isDragging immediately for touch either

    pointMoveTo({ x, y });

    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: false });
  };

  const onTouchMove = (e: any) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    const rect = node?.current?.getBoundingClientRect();
    const rootDistance = y - (rect?.y || rect?.top || 0);

    // Now we know user is actually dragging
    setIsDragging(true);

    if (rootDistance > 80 && stops.length > 2) {
      setHideStop(true);
      return;
    } else {
      setHideStop(false);
    }

    pointMoveTo({
      x,
      y
    });
  };

  const onTouchEnd = () => {
    setIsDragging(false); // Mark as not dragging
    removeTouchListeners();
  };

  const pointMoveTo = (coords: TCoords) => {
    const rect = node && node.current.getBoundingClientRect();
    const width = rect.width;
    let pos = coords.x - rect.left;
    pos = Math.max(0, pos);
    pos = Math.min(pos, width);

    const location = Number(((100 / rect.width) * pos).toFixed(0)) / 100;
    setActiveColor((prev) => ({
      ...prev,
      loc: location
    }));
  };

  const deleteColorStop = () => {
    if (stops.length <= 2) return;
    const newStops = stops
      .filter((stop: [string, number, number]) => stop[2] !== activeColor.index)
      .map((stop: [string, number, number], index: number) => {
        stop[2] = index;
        return stop;
      });
    const lastStop = rgbaToArray(newStops[newStops.length - 1][0]);
    const lastStopLoc = newStops[newStops.length - 1][1];
    const activeStop = rgbaToHex([lastStop[0], lastStop[1], lastStop[2]]);
    const activeIdx = newStops[newStops.length - 1][2];

    setNeedDeleteActive(false);
    setHideStop(false);

    setActiveColor({
      hex: activeStop,
      alpha: Number(Math.round(lastStop[3] * 100)),
      loc: lastStopLoc,
      index: activeIdx
    });
    return setColor({
      ...color,
      gradient: `${getGradient(type, newStops, modifier, format, showAlpha)}`,
      stops: newStops
    });
  };

  useEffect(() => {
    if (needDeleteActive) {
      return deleteColorStop();
    }

    // Only update positions when actually dragging, not when just changing colors
    if (isDragging) {
      // Check if the position actually changed
      const currentStop = stops.find((item) => item[2] === activeColor.index);
      if (currentStop && currentStop[1] !== activeColor.loc) {
        const newStops = stops.map((item: [string, number, number]) => {
          if (activeColor.index === item[2]) {
            return [item[0], activeColor.loc, item[2]];
          }
          return item;
        });

        setColor({
          ...color,
          gradient: `${getGradient(
            type,
            newStops,
            modifier,
            format,
            showAlpha
          )}`,
          stops: newStops
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeColor.loc, needDeleteActive, isDragging]);

  useEffect(() => {
    return () => {
      removeListeners();
      removeTouchListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className='gradient-stops'
      onClick={(e) => onAddColorStop(e)}
      ref={node}
    >
      <div
        className='gradient-stop-preview'
        style={{
          background: `linear-gradient(to right, ${stops
            .map(
              (color: [string, number, number]) =>
                `${color[0]} ${color[1] * 100}%`
            )
            .join(', ')})`
        }}
      />
      <div className='gradient-stop-marker'>
        {stops.map((color: [string, number, number]) => {
          const position = color[1] * 100;
          const rgba = color[0];

          return (
            <div
              key={rgba + position + Math.random() * 100}
              className={`gradient-marker${
                hideStop && activeColor.index === color[2] ? ' hide' : ''
              }${!hideStop && activeColor.index === color[2] ? ' active' : ''}`}
              style={{
                left: Math.abs(Math.min(position, 100)) + '%',
                color: rgba
              }}
              onTouchStart={(e) => onTouchStart(e, color)}
              onMouseDown={(e) => onMouseDown(e, color)}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={deleteColorStop}
            />
          );
        })}
      </div>
    </div>
  );
};

export default Markers;
